import { TrendingUp, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { InvoiceDetail, InvoiceSaleTypeValue } from '../../types'

interface InvoiceCommissionSectionProps {
    invoice: InvoiceDetail
}

/** Sale type color mapping */
const getSaleTypeStyles = (saleType: InvoiceSaleTypeValue | undefined) => {
    switch (saleType) {
        case 'renewable':
            return {
                card: 'bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-100 shadow-sky-100/50',
                iconBg: 'bg-sky-100',
                iconColor: 'text-sky-600',
                badge: 'bg-sky-100 text-sky-700',
            }
        case 'new-sell':
            return {
                card: 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100 shadow-emerald-100/50',
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-600',
                badge: 'bg-emerald-100 text-emerald-700',
            }
        default:
            return {
                card: 'bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100 shadow-violet-100/50',
                iconBg: 'bg-violet-100',
                iconColor: 'text-violet-600',
                badge: 'bg-violet-100 text-violet-700',
            }
    }
}

const InvoiceCommissionSection: React.FC<InvoiceCommissionSectionProps> = ({
    invoice,
}) => {
    const { t } = useTranslation()

    if (!invoice?.employee_names && !invoice?.invoice_sale_type) return null

    const saleType = invoice?.invoice_sale_type?.invoice_sale_type
    const styles = getSaleTypeStyles(saleType)
    const saleTypeTranslationKey: Record<InvoiceSaleTypeValue, string> = {
        'new-sell': 'sale_type.new_sale',
        renewable: 'sale_type.renewable',
        upsell: 'sale_type.upsale',
    }

    return (
        <div
            data-tour="invoice-commission"
            className="bg-white rounded-sm p-4 space-y-2"
        >
            <Typogaphy className="text-primary font-medium capitalize">
                {t('commission_employees')}
            </Typogaphy>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Employee Commission Card */}
                {invoice?.employee_names && (
                    <div
                        data-tour="commission-employee"
                        className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-sm px-3 py-2.5 border border-blue-100 shadow-sm shadow-blue-100/50 flex flex-col gap-1"
                    >
                        <div className="flex items-center gap-1.5 mb-1">
                            <div className="p-1.5 bg-blue-100 rounded-md">
                                <Users className="w-3.5 h-3.5 text-blue-600" />
                            </div>
                            <Typogaphy className="text-xs text-gray-500 capitalize">
                                {t('commission_employees')}
                            </Typogaphy>
                        </div>
                        <Typogaphy className="text-sm font-medium text-gray-800">
                            {invoice?.employee_names}
                        </Typogaphy>
                        {invoice?.employee_codes && (
                            <Typogaphy className="text-[10px] text-gray-400 mt-0.5">
                                {invoice?.employee_codes}
                            </Typogaphy>
                        )}
                    </div>
                )}
                {/* Sale Type Card */}
                {invoice?.invoice_sale_type && (
                    <div
                        data-tour="commission-sale-type"
                        className={`rounded-sm px-3 py-2.5 border shadow-sm ${styles.card}`}
                    >
                        <div className="flex items-center gap-1.5 mb-1">
                            <div
                                className={`p-1.5 rounded-md ${styles.iconBg}`}
                            >
                                <TrendingUp
                                    className={`w-3.5 h-3.5 ${styles.iconColor}`}
                                />
                            </div>
                            <Typogaphy className="text-xs text-gray-500 capitalize">
                                {t('invoice_sale_type')}
                            </Typogaphy>
                        </div>
                        <Typogaphy
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${styles.badge}`}
                        >
                            {saleType
                                ? t(saleTypeTranslationKey[saleType])
                                : '---'}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default InvoiceCommissionSection
